import {
  QueryClient,
  queryOptions,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { QueryRefetchInterval } from '@cast/shared-types';
import {
  SecurityInsightsAgentStatus,
  SecurityInsightsAgents,
} from '@cast/types';
import { isDemoCluster, makeIsEnabled } from '@cast/utils';

import { apiClient } from '@cast/infrastructure-api';

import { getSecurityClustersSettingsQueryKey } from './useSecurityClustersSettings';
import { getSecurityInsightsAgentsQueryKey } from './useSecurityInsightsAgents';

type UseSecurityInsightsAgentQueryArgs = {
  clusterId?: string;
  refetchInterval?: QueryRefetchInterval<SecurityInsightsAgentStatus>;
  enabled?: boolean;
};

const SECURITY_AGENT_STATUS_QUERY_KEY = 'security-agent-status';

const getSecurityInsightsAgentQueryKey = (clusterId?: string) =>
  [SECURITY_AGENT_STATUS_QUERY_KEY, clusterId] as const;

const getSecurityInsightsAgentQueryOptions = ({
  clusterId,
  refetchInterval = false,
  enabled = true,
  client,
}: UseSecurityInsightsAgentQueryArgs & { client: QueryClient }) => {
  const queryKey = getSecurityInsightsAgentQueryKey(clusterId);
  return queryOptions({
    queryKey,
    queryFn: async () => {
      const oldData =
        client.getQueryData<SecurityInsightsAgentStatus>(queryKey);
      const { data } = await apiClient.securityInsights.getAgentStatus({
        clusterId: clusterId!,
      });

      // Update the SECURITY_AGENT_STATUSES cache with this cluster's status
      // Note: setHasEnabledSecurityAgents is called by the security-insights middleware
      // when getAgentsStatuses (plural) is called
      client.setQueriesData(
        {
          queryKey: getSecurityInsightsAgentsQueryKey(),
        },
        (
          agents: SecurityInsightsAgents | undefined = {}
        ): SecurityInsightsAgents => ({
          agentStatuses: {
            ...agents?.agentStatuses,
            [clusterId!]: !!data.installed,
          },
          statuses: {
            ...agents?.statuses,
            [clusterId!]: data,
          },
        })
      );

      if (!oldData?.installed) {
        client.invalidateQueries({
          queryKey: getSecurityClustersSettingsQueryKey(),
        });
      }

      return data;
    },
    enabled: makeIsEnabled(
      enabled,
      !!clusterId,
      !!clusterId && !isDemoCluster(clusterId)
    ),
    refetchInterval,
  });
};

export const useSecurityInsightsAgentQuery = ({
  clusterId,
  refetchInterval = false,
  enabled = true,
}: UseSecurityInsightsAgentQueryArgs) => {
  const client = useQueryClient();

  const {
    data: agent,
    isLoading,
    error,
    refetch,
  } = useQuery(
    getSecurityInsightsAgentQueryOptions({
      clusterId,
      refetchInterval,
      enabled,
      client,
    })
  );
  return { agent, isLoading, error, refetch };
};
